import {
  Box, Button, Checkbox, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, MenuItem, TextField, Typography, Divider, Alert, Radio, RadioGroup, FormLabel
} from '@mui/material';
import React, { FC, useEffect, useState, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import CustomSelect, { IOptions } from '../FormFields/CustomSelect';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import LoadingButton from '@mui/lab/LoadingButton';
import { ApiStatus } from "../../utils/api.types";
import { fetchEmployees } from '../../store/employeeStore/employeeThunk';
import { calculateDailyWage, processDailyWage } from '../../store/salaryStore/salaryThunk';
import { fetchTaxRates } from '../../store/gstStore/gstThunk';
import { fetchBanks } from '../../store/bankStore/bankThunk';
import { fetchCostCentres } from '../../store/costCentreStore/costCentreThunk';

interface DialogProcessDailyWageFormProps {
  title: String;
  open: boolean;
  onClose: (refresh: any) => void;
}

interface ITdsOption extends IOptions {
  rate?: number;
  taxSection?: string;
  taxName?: string;
}

const DialogProcessDailyWageForm: FC<DialogProcessDailyWageFormProps> = ({ onClose, open, title }) => {

  const { status, salaryCalculation } = useAppSelector(state => state.salaries);
  const { selectedAccount } = useAppSelector(state => state.auth);
  const { employees } = useAppSelector(state => state.employees);
  const { costCentres } = useAppSelector(state => state.costCentres);
  const dispatch = useAppDispatch();

  // Form states
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(new Date().toISOString().slice(0, 10));

  // Wage calculation states
  const [calculationMethod, setCalculationMethod] = useState<'PER_DAY' | 'PER_HOUR'>('PER_DAY');
  const [ratePerDay, setRatePerDay] = useState(0);
  const [daysWorked, setDaysWorked] = useState(0);
  const [ratePerHour, setRatePerHour] = useState(0);
  const [hoursWorked, setHoursWorked] = useState(0);
  const [overtimeHours, setOvertimeHours] = useState(0);
  const [overtimeRate, setOvertimeRate] = useState(0);

  // Bank account states
  const [selectedFromBankAccount, setSelectedFromBankAccount] = useState('');
  const [fromBankAccountOptions, setFromBankAccountOptions] = useState<IOptions[]>([]);

  // Cost centre state
  const [selectedCostCentre, setSelectedCostCentre] = useState('');

  // Payment mode states
  const [paymentMode, setPaymentMode] = useState('CASH');
  const [paymentStatus, setPaymentStatus] = useState('PROCESSED');

  // Tax configuration states
  const [applyTax, setApplyTax] = useState(false);

  // TDS states
  const [tdsTaxRateOptions, setTdsTaxRateOptions] = useState<ITdsOption[]>([]);
  const [tdsSelected, setTdsSelected] = useState({
    _id: "",
    label: "",
    rate: 0
  });

  // Form validation schema
  const schema = yup.object().shape({
    employeeId: yup.string().required("Employee is required."),
  });

  const methods = useForm<any>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema)
  });

  // Load TDS rates once when form opens
  useEffect(() => {
    if (!open) return;

    let isMounted = true;

    const getTdsRates = async () => {
      try {
        const taxRatesData: any = await dispatch(fetchTaxRates()).unwrap();
        if (taxRatesData && taxRatesData.length > 0 && isMounted) {
          const tdsRates = taxRatesData.filter((item: any) => item.taxType === "tds");
          const tdsOptions = tdsRates.map((item: any) => ({
            label: `${item.taxRate}% - ${item.taxSection} ${item.taxName}`,
            value: item._id,
            rate: item.taxRate,
            taxSection: item.taxSection,
            taxName: item.taxName
          }));
          setTdsTaxRateOptions(tdsOptions);
        }
      } catch (error) {
        console.error('Error fetching TDS rates:', error);
      }
    };

    getTdsRates();

    return () => {
      isMounted = false;
    };
  }, [dispatch, open]);

  // Load employees, banks, and cost centres when form opens
  useEffect(() => {
    if (!open || !selectedAccount?._id) return;

    let isMounted = true;

    const loadData = async () => {
      try {
        // Load employees
        await dispatch(fetchEmployees({
          accountId: selectedAccount._id,
          type: 'list',
          params: ''
        }));

        // Load bank accounts
        const bankData: any = await dispatch(fetchBanks(selectedAccount._id));
        if (bankData.payload?.banks?.length > 0 && isMounted) {
          const bankOptions = bankData.payload.banks.map((c: any) => ({
            label: c.accountName + '-' + c.bankName,
            value: c._id
          }));
          setFromBankAccountOptions(bankOptions);

          const firstBank = bankData.payload.banks[0];
          methods.setValue('fromBankAccount', firstBank._id);
          setSelectedFromBankAccount(firstBank._id);
        }

        // Load cost centres (only those enabled for payroll)
        await dispatch(fetchCostCentres({ activeOnly: true, useForPayroll: true }));
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [selectedAccount?._id, open, dispatch]);

  // Calculate days worked when dates change
  useEffect(() => {
    if (fromDate && toDate && calculationMethod === 'PER_DAY') {
      const from = new Date(fromDate);
      const to = new Date(toDate);

      // Calculate the difference in days (inclusive)
      const timeDiff = to.getTime() - from.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both dates

      // Ensure we don't set negative days
      setDaysWorked(daysDiff >= 0 ? daysDiff : 0);
    }
  }, [fromDate, toDate, calculationMethod]);

  // Watch for employee selection changes and auto-populate cost centre
  useEffect(() => {
    const subscription = methods.watch((value, { name }) => {
      if (name === 'employeeId' && value.employeeId) {
        const selectedEmployee = employees.find((emp: any) => emp._id === value.employeeId);
        if (selectedEmployee) {
          const empData: any = selectedEmployee.employee || selectedEmployee;
          // Auto-populate cost centre from employee's default
          if (empData.costCentre) {
            setSelectedCostCentre(empData.costCentre);
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [methods, employees]);

  // Auto-change payment status to PAID when CASH is selected
  useEffect(() => {
    if (paymentMode === 'CASH' && paymentStatus === 'PROCESSED') {
      setPaymentStatus('PAID');
    }
  }, [paymentMode, paymentStatus]);

  // Memoize TDS options
  const tdsOptionsWithDefault = useMemo(() => {
    return [{ label: 'Default (10% if > ₹15K)', value: '', rate: 0 }, ...tdsTaxRateOptions];
  }, [tdsTaxRateOptions]);

  // Handle TDS selection change
  const handleTdsChange = (event: any) => {
    const selectedTdsId = event.target.value;

    if (!selectedTdsId) {
      setTdsSelected({ _id: "", label: "Default TDS", rate: 0 });
      return;
    }

    const selectedTdsOption = tdsTaxRateOptions.find((opt: ITdsOption) => opt.value === selectedTdsId);
    if (selectedTdsOption) {
      setTdsSelected({
        _id: selectedTdsOption.value,
        label: selectedTdsOption.label,
        rate: selectedTdsOption.rate || 0
      });
    }
  };

  // Handle calculate button
  const handleCalculate = async () => {
    const employeeId = methods.getValues('employeeId');
    if (!employeeId) {
      alert('Please select an employee');
      return;
    }

    const wageData = {
      calculationMethod,
      ratePerDay,
      daysWorked,
      ratePerHour,
      hoursWorked,
      overtimeHours,
      overtimeRate,
      applyTax,
      tdsConfig: {
        apply: applyTax,
        rate: tdsSelected.rate,
        label: tdsSelected.label
      }
    };

    await dispatch(calculateDailyWage({
      employeeId,
      wageData
    }));
  };

  // Handle form submit
  const onSubmit = async (data: any) => {
    try {
      setFormSubmitted(true);

      const wageData = {
        calculationMethod,
        ratePerDay,
        daysWorked,
        ratePerHour,
        hoursWorked,
        overtimeHours,
        overtimeRate,
        applyTax,
        tdsConfig: {
          apply: applyTax,
          rate: tdsSelected.rate,
          label: tdsSelected.label
        }
      };

      const paymentOptions = {
        paymentStatus: paymentStatus as 'PROCESSED' | 'PAID',
        paymentMode,
        fromBankAccount: selectedFromBankAccount,
        remarks: data.remarks
      };

      await dispatch(processDailyWage({
        employeeId: data.employeeId,
        fromDate: new Date(fromDate),
        toDate: new Date(toDate),
        costCentre: selectedCostCentre || undefined,
        wageData,
        paymentOptions
      })).unwrap();

      methods.reset();
      setFormSubmitted(false);
      onClose(true);
    } catch (error: any) {
      setFormSubmitted(false);
      console.error('Error processing daily wage:', error);
    }
  };

  const handleClose = () => {
    methods.reset();
    setFormSubmitted(false);
    onClose(false);
  };

  const wageCalculation = salaryCalculation as any;

  return (
    <div className="drawer-form-container" style={{ width: "100%", maxWidth: "800px" }}>
      <FormProvider {...methods}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            padding: "2px 0 0 2px",
            position: "relative"
          }}
          component="form"
          noValidate
        >
          <DialogTitle sx={{ py: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.05rem' }}>
                Process Daily Wage Payment
              </Typography>
            </Box>
          </DialogTitle>

          <DialogContent sx={{
            padding: '16px 24px',
            paddingBottom: '90px',
            overflowY: 'auto',
            flex: 1
          }}>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {/* Employee Selection */}
              <Grid item xs={12}>
                <Controller
                  name="employeeId"
                  control={methods.control}
                  render={({ field }) => (
                    <CustomSelect
                      {...field}
                      id="employeeId"
                      label="Select Employee"
                      size="small"
                      options={employees.map((emp: any) => ({
                        label: `${emp.name} (${emp.empId})`,
                        value: emp._id
                      }))}
                      error={Boolean(methods.formState.errors.employeeId)}
                      helperText={methods.formState.errors.employeeId?.message as string}
                    />
                  )}
                />
              </Grid>

              {/* Cost Centre Selection - Only show if cost centres exist */}
              {costCentres && costCentres.length > 0 && (
                <Grid item xs={12}>
                  <CustomSelect
                    name="costCentre"
                    id="costCentre"
                    label="Cost Centre (Optional)"
                    size="small"
                    fullWidth
                    options={costCentres.map((cc: any) => ({
                      label: cc.alias ? `${cc.name} (${cc.alias})` : cc.name,
                      value: cc._id
                    }))}
                    value={selectedCostCentre}
                    onChange={(e: any) => setSelectedCostCentre(e.target.value)}
                  />
                </Grid>
              )}

              {/* Calculation Method */}
              <Grid item xs={12}>
                <FormLabel>Calculation Method</FormLabel>
                <RadioGroup
                  row
                  value={calculationMethod}
                  onChange={(e) => setCalculationMethod(e.target.value as 'PER_DAY' | 'PER_HOUR')}
                >
                  <FormControlLabel value="PER_DAY" control={<Radio />} label="Per Day" />
                  <FormControlLabel value="PER_HOUR" control={<Radio />} label="Per Hour" />
                </RadioGroup>
              </Grid>

              {/* Date Range */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="From Date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="To Date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>

              {/* Per Day Fields */}
              {calculationMethod === 'PER_DAY' && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Rate Per Day (₹)"
                      value={ratePerDay}
                      onChange={(e) => setRatePerDay(parseFloat(e.target.value))}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Days Worked"
                      value={daysWorked}
                      onChange={(e) => setDaysWorked(parseFloat(e.target.value))}
                      size="small"
                    />
                  </Grid>
                </>
              )}

              {/* Per Hour Fields */}
              {calculationMethod === 'PER_HOUR' && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Rate Per Hour (₹)"
                      value={ratePerHour}
                      onChange={(e) => setRatePerHour(parseFloat(e.target.value))}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Hours Worked"
                      value={hoursWorked}
                      onChange={(e) => setHoursWorked(parseFloat(e.target.value))}
                      size="small"
                    />
                  </Grid>
                </>
              )}

              {/* Overtime Fields */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Overtime Hours"
                  value={overtimeHours}
                  onChange={(e) => setOvertimeHours(parseFloat(e.target.value))}
                  size="small"
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Overtime Rate (₹/hour)"
                  value={overtimeRate}
                  onChange={(e) => setOvertimeRate(parseFloat(e.target.value))}
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Tax Configuration :</Typography>
              </Grid>

              {/* Tax Checkbox */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={applyTax} onChange={(e) => setApplyTax(e.target.checked)} />}
                  label="Apply TDS Deduction"
                />
              </Grid>

              {applyTax && (
                <Grid item xs={12}>
                  <CustomSelect
                    name="tdsRate"
                    id="tdsRate"
                    label="TDS Rate"
                    value={tdsSelected._id}
                    onChange={handleTdsChange}
                    options={tdsOptionsWithDefault}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Payment Status */}
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Payment Status"
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                  size="small"
                >
                  <MenuItem value="PROCESSED" disabled={paymentMode === 'CASH'}>
                    Processed {paymentMode === 'CASH' && '(Not available for Cash)'}
                  </MenuItem>
                  <MenuItem value="PAID">Paid</MenuItem>
                </TextField>
              </Grid>

              {/* Payment Mode */}
              <Grid item xs={6}>
                <TextField
                  select
                  fullWidth
                  label="Payment Mode"
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  size="small"
                >
                  <MenuItem value="CASH">Cash</MenuItem>
                  <MenuItem value="NEFT">NEFT</MenuItem>
                  <MenuItem value="RTGS">RTGS</MenuItem>
                  <MenuItem value="UPI">UPI</MenuItem>
                  <MenuItem value="CHEQUE">Cheque</MenuItem>
                </TextField>
              </Grid>

              {/* Bank Account */}
              {paymentMode !== 'CASH' && (
                <Grid item xs={12}>
                  <CustomSelect
                    name="fromBankAccount"
                    id="fromBankAccount"
                    label="From Bank Account"
                    value={selectedFromBankAccount}
                    onChange={(e: any) => setSelectedFromBankAccount(e.target.value)}
                    options={fromBankAccountOptions}
                  />
                </Grid>
              )}

              {/* Remarks */}
              <Grid item xs={12}>
                <Controller
                  name="remarks"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Remarks (Optional)"
                      multiline
                      rows={2}
                      size="small"
                    />
                  )}
                />
              </Grid>

              {/* Wage Calculation Preview */}
              {wageCalculation && wageCalculation.summary && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2">Wage Breakdown</Typography>
                    <Typography variant="body2">Gross: ₹{wageCalculation.summary.grossWage?.toLocaleString('en-IN') || 0}</Typography>
                    <Typography variant="body2">TDS: ₹{wageCalculation.deductions?.tds?.toLocaleString('en-IN') || 0}</Typography>
                    <Typography variant="body1" fontWeight="bold">Net Payable: ₹{wageCalculation.summary.netWage?.toLocaleString('en-IN') || 0}</Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#fff',
            borderTop: '1px solid #c1c5cb',
            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
            padding: '12px 24px',
            margin: 0,
            zIndex: 1000
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              width: '100%'
            }}>
              <Button
                variant='outlined'
                onClick={handleClose}
                sx={{
                  minWidth: '100px',
                  height: '40px'
                }}
              >
                Cancel
              </Button>

              <LoadingButton
                type="button"
                onClick={methods.handleSubmit(onSubmit)}
                variant="contained"
                loading={formSubmitted || status === ApiStatus.LOADING}
                sx={{
                  minWidth: '180px',
                  height: '40px'
                }}
              >
                Process Daily Wage
              </LoadingButton>
            </Box>
          </DialogActions>
        </Box>
      </FormProvider>
    </div>
  );
};

export default DialogProcessDailyWageForm;
